import React from "react";
import ErrorMessage from "../common-components/ErrorMessage";
import RadioCheckboxInput from "../common-components/RadioCheckboxInput";
import { EvaluationFormData, Interviewer } from "./EvaluationSheet";
import { DatePicker } from "antd";
import dayjs from "dayjs";
// import { Interviewer, EvaluationFormData } from "./EvaluationSheet.types";

// ─── Constants
const selectionOptions = ["Select", "Reject", "Hold"];
const recommendationOptions = ["Recommended", "Not Recommended", "Put on Hold"];

const ratingParams: { label: string; key: keyof Interviewer }[] = [
  {
    label: "Strong Functional / Technical Knowledge:",
    key: "rating_technical_knowledge",
  },
  {
    label: "Good functional Qualification:",
    key: "rating_functional_qualification",
  },
  { label: "Communication Skills:", key: "rating_communication_skills" },
  { label: "Confidence Level:", key: "rating_confidence_level" },
  { label: "Body Language:", key: "rating_body_language" },
];

// ─── Props
interface InterviewerBlockProps {
  label: string;
  prefix: keyof Pick<
    EvaluationFormData,
    "interviewer1" | "interviewer2" | "hrHead"
  >;
  formData: Interviewer;
  errors?: Partial<Record<keyof Interviewer, string>>;
  onChange: (prefix: string, field: keyof Interviewer, value: string) => void;
  handleDateChange: (name: string, value: string) => void;
}

// ─── Rating Radio Row
const RatingRow = ({
  prefix,
  param,
  value,
  error,
  onChange,
}: {
  prefix: string;
  param: { label: string; key: keyof Interviewer };
  value: string;
  error?: string;
  onChange: (field: keyof Interviewer, value: string) => void;
}) => (
  <td colSpan={2} style={{ color: "#1f2937", fontSize: "0.82rem" }}>
    {param.label}
    <div className="d-flex gap-2 mt-1">
      {[1, 2, 3, 4].map((n) => (
        <label key={n} className="d-flex align-items-center gap-1">
          <input
            type="radio"
            name={`${prefix}.${param.key as string}`}
            value={n}
            checked={value === String(n)}
            onChange={() => onChange(param.key, String(n))}
          />
          {n}
        </label>
      ))}
    </div>
    <ErrorMessage message={error} />
  </td>
);

// ─── Component ────────────────────────────────────────────────────────────────
const Eval_InterviewerBlock = ({
  label,
  prefix,
  formData,
  errors,
  onChange,
  handleDateChange,
}: InterviewerBlockProps) => {
  const change = (field: keyof Interviewer, value: string) =>
    onChange(prefix, field, value);

  return (
    <div className="table-responsive mb-4">
      <table className="form-table">
        <thead>
          <tr>
            <th colSpan={3} className="text-center">
              Selection Criteria
            </th>
          </tr>
        </thead>
        <tbody>
          {/* ── Label + Select/Reject/Hold + Date & Time */}
          <tr>
            <td className="td-label fw-semibold" style={{ width: 200 }}>
              {label}&nbsp;<span className="text-danger">*</span>
            </td>
            <td style={{ width: 160 }}>
              {selectionOptions?.map((opt) => (
                <div
                  key={opt}
                  className="single-checked-input d-flex align-items-center gap-1"
                >
                  <input
                    type="radio"
                    name={`${prefix}.selection_criteria`}
                    id={`${prefix}-${opt}`}
                    value={opt}
                    checked={formData.selection_criteria === opt}
                    onChange={() => change("selection_criteria", opt)}
                  />
                  <label htmlFor={`${prefix}-${opt}`} className="ps-2">
                    {opt}
                  </label>
                </div>
              ))}
              <ErrorMessage message={errors?.selection_criteria} />
            </td>
            <td>
              <span
                className="fw-semibold me-2"
                style={{ fontSize: "0.82rem" }}
              >
                Date & Time:&nbsp;<span className="text-danger">*</span>
              </span>
              <div className="d-inline-flex align-items-center ">
                <DatePicker
                  showTime
                  name={`${prefix}.interview_datetime`}
                  style={{
                    width: "200px",
                  }}
                  className="form-control d-inline datetimepicker inline col-4 ms-2"
                  format={{
                    format: "DD-MM-YYYY HH:mm:ss",
                    type: "mask",
                  }}
                  placeholder="DD-MM-YYYY HH:mm:ss"
                  disabledDate={(current) =>
                    current && current.isAfter(dayjs(), "date")
                  }
                  value={
                    formData.interview_datetime // ← direct access
                      ? dayjs(
                          formData.interview_datetime,
                          "YYYY-MM-DD HH:mm:ss",
                        )
                      : null
                  }
                  onChange={(date: any, dateString: string | string[]) => {
                    if (!date) return;
                    change(
                      "interview_datetime",
                      date?.format("YYYY-MM-DD HH:mm:ss"),
                    );
                  }}
                />
                {/* <ErrorMessage message={errors?]} classStyle="ms-2" /> */}
              </div>
              <ErrorMessage message={errors?.interview_datetime} />
            </td>
          </tr>

          {/* ── Name */}
          <tr>
            <td className="td-label">
              Name:&nbsp;<span className="text-danger">*</span>
            </td>
            <td colSpan={2}>
              <input
                value={formData.interviewer_name}
                onChange={(e) => change("interviewer_name", e.target.value)}
                className="w-100"
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: "0.82rem",
                }}
              />
              <ErrorMessage message={errors?.interviewer_name} />
            </td>
          </tr>

          {/* ── Rating params */}
          <tr>
            <td
              className="td-label"
              rowSpan={ratingParams.length + 2}
              style={{ verticalAlign: "top", paddingTop: 10 }}
            >
              Please rate each parameter on a scale from 1–4&nbsp;
              <span className="text-danger">*</span>
            </td>
            <RatingRow
              prefix={prefix}
              param={ratingParams[0]}
              value={formData[ratingParams[0].key]}
              error={errors?.[ratingParams[0].key]}
              onChange={change}
            />
          </tr>
          {ratingParams.slice(1).map((param) => (
            <tr key={param.key as string}>
              <RatingRow
                prefix={prefix}
                param={param}
                value={formData[param.key]}
                error={errors?.[param.key]}
                onChange={change}
              />
            </tr>
          ))}

          {/* ── Overall Score */}
          <tr>
            <td colSpan={2}>
              <span
                className="fw-semibold"
                style={{ fontSize: "0.82rem", color: "#111827" }}
              >
                Overall Score:&nbsp;<span className="text-danger">*</span>
              </span>
            </td>
          </tr>

          {/* ── Recommendation */}
          <tr>
            <td colSpan={3}>
              <div className="d-flex gap-4">
                {recommendationOptions.map((opt) => (
                  <RadioCheckboxInput
                    key={opt}
                    id={`${prefix}-rec-${opt}`}
                    name={`${prefix}.recommendation`}
                    value={opt}
                    label={opt}
                    checked={formData.recommendation === opt}
                    onChange={() => change("recommendation", opt)}
                  />
                ))}
              </div>
              <ErrorMessage message={errors?.recommendation} />
            </td>
          </tr>

          {/* ── Remarks */}
          <tr>
            <td colSpan={3}>
              <span
                className="fw-semibold"
                style={{ fontSize: "0.82rem", color: "#111827" }}
              >
                Remarks:&nbsp;<span className="text-danger">*</span>
              </span>
              <input
                value={formData.remarks}
                onChange={(e) => change("remarks", e.target.value)}
                className="w-100 mt-1 mb-3"
                style={{
                  border: "none",
                  borderBottom: "1px solid #838383",
                  outline: "none",
                  background: "transparent",
                  fontSize: "0.82rem",
                }}
              />
              <ErrorMessage message={errors?.remarks} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default Eval_InterviewerBlock;
